import React from 'react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import { FormattedMessage } from '../../util/reactIntl';
import { hasNoSmsCredit } from '../../ducks/user.duck';
import { credentialTrustNotice } from './credentialTrustNotice';

const BUY_MESSAGE_PACK_PATH = '/account/message-packs';
const GENERIC_SHARE_ERROR = 'Failed to share credentials. Please try again.';

// Shown next to the "By SMS" option of the credential dialogs when the server's balance says there
// is no SMS credit left. It only informs: the option stays selectable, and the server decides.
export const NoSmsCreditsHint = ({ smsBalance }) =>
  hasNoSmsCredit(smsBalance) ? (
    <span style={{ marginLeft: '8px', fontSize: '12px', color: '#8c8c8c' }}>
      <FormattedMessage id="SmsCredit.noCreditsLeft" />{' '}
      <a href={BUY_MESSAGE_PACK_PATH}>
        <FormattedMessage id="SmsCredit.buyMessagePack" />
      </a>
    </span>
  ) : null;

// The recipient's phone number in the credential share dialogs. A number typed without its country
// code is read as the country picked, US by default, and handed back in international form
// ("+14155550123"), which is what the server texts; '' when the field is cleared. Every country is
// offered, because the recipient is whoever the sharer chooses.
export const SharePhoneInput = ({ value, onChange, ...rest }) => (
  <PhoneInput defaultCountry="US" value={value || ''} onChange={v => onChange(v || '')} {...rest} />
);

// Whether a share may be submitted with this number; the server still decides whether it can be texted.
export const isSharePhoneValid = value => !!value && isValidPhoneNumber(value);

// Submits a credential share and tells the sharer how it went. The share is always submitted:
// whether its text message can be paid for is decided by the server, which skips only the SMS
// when email is also chosen, and refuses an SMS-only share without creating anything.
// `notify` is { success, warning, error }, each taking the text to show.
export const submitCredentialShare = async ({ shareData, onShareCredential, intl, notify }) => {
  const byEmail = !!shareData?.deliveryPreference?.byEmail;
  const bySms = !!shareData?.deliveryPreference?.bySMS;

  try {
    const response = await onShareCredential(shareData);
    if (!response?.success) {
      notify.error(response?.error || 'Failed to share credentials.');
      return response;
    }

    const sms = response.sms;
    if (bySms && sms?.sms === 'skipped' && sms.reason === 'no-credit') {
      notify.warning(intl.formatMessage({ id: 'CredentialShare.smsSkippedNoCredit' }));
      return response;
    }

    // Only claim an SMS the server reported as sent.
    const smsSent = bySms && sms?.sms === 'sent';
    notify.success(
      `Credentials shared successfully! The recipient has been notified${
        byEmail ? ' via email' : ''
      }${byEmail && smsSent ? ' and' : ''}${smsSent ? ' via SMS' : ''}.`
    );
    return response;
  } catch (error) {
    const trustNotice = credentialTrustNotice(error, intl);
    if (trustNotice) {
      notify.warning(trustNotice);
    } else if (error?.error === 'no-sms-credit') {
      notify.error(intl.formatMessage({ id: 'CredentialShare.smsOnlyNoCredit' }));
    } else if (error?.error === 'sms-number-not-textable') {
      notify.error(intl.formatMessage({ id: 'CredentialShare.smsNumberNotTextable' }));
    } else if (error?.error === 'sms-unavailable') {
      notify.error(intl.formatMessage({ id: 'CredentialShare.smsCreditUnavailable' }));
    } else {
      notify.error(error?.error || error?.details || error?.message || GENERIC_SHARE_ERROR);
    }
    return null;
  }
};
